import { useState, useCallback, useRef, useEffect, Fragment, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useApi } from '../../../../hooks/useApi';
import { usePlanFeatures } from '../../../../contexts/PlanFeaturesContext';
import {
  loadPriceProducts, fetchPrices, updatePrices,
  getRepricerConfig, saveRepricerConfig, applyRepricerManual,
  getRepricerRuns, getRepricerRunItems,
  bulkChangePrice, createPriceSchedule, listPriceSchedules, cancelPriceSchedule,
  fetchParserCurrentPrices, listPriceChangeLog,
  deactivatePromotionProducts, activatePromotionProducts,
  getPromotionAutomationConfig, savePromotionAutomationConfig,
  runPromotionAutomationNow, listPromotionAutomationLog,
  getPromotionsOverview, savePromotionProductSetting,
  runPromotionAutomationExitAll, runPromotionAutomationJoinAll, runPromotionAutomationJoinWithBoundedExit,
  mapRepricerListProducts,
} from '../../../../services/ozonPrices';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import PriceChangeModal from './PriceChangeModal';
import OzonProfitabilityPanel from './OzonProfitabilityPanel';
import OzonVitrineMonitor from './OzonVitrineMonitor';
import MapRepricerPanel from './MapRepricerPanel';
import OzonPromoPrices from './OzonPromoPrices';
import './OzonPricesPanel.css';
import ToolHelpButton from '../../../../components/ToolHelpButton';
import { fmtDate, fmtDateTime } from '../../../../utils/ruDates';
import { matchesSearch, filterBySearch } from '../../../../utils/search';
import { useUrlParams } from '../../../../hooks/useUrlParams';

function uid() { return String(Date.now()) + String(Math.floor(Math.random() * 1e6)); }

function fmtDT(iso) {
  if (!iso) return '—';
  try {
    return fmtDateTime(iso);
  } catch { return iso; }
}

const COLOR_INDEX_LABELS = {
  WITHOUT_INDEX: 'Без индекса',
  RED: 'Не выгодный',
  YELLOW: 'Умеренный',
  GREEN: 'Выгодный',
  SUPER: 'Супер выгодный',
};

function formatVitrinePrice(pr, promo) {
  if (promo == null) return { text: pr.marketing_price || '', stale: false };
  const value = promo.price;
  if (value == null) return { text: pr.marketing_price || '', stale: false };
  const inStock = Number(pr.stock) > 0;
  if (pr.price && inStock) return { text: String(value), stale: false };
  const dateStr = promo.checked_at
    ? fmtDate(promo.checked_at)
    : null;
  return { text: dateStr ? `${value} (на ${dateStr})` : String(value), stale: true };
}

function slotLabel(name) {
  if (name === 'night') return 'Ночной';
  if (name === 'day') return 'Дневной';
  return name;
}

function parseOzonPriceResult(result, prices) {
  const items = result?.result || [];
  const byId = {};
  items.forEach(i => { byId[String(i.offer_id)] = i; });
  const details = prices.map(p => {
    const r = byId[String(p.offer_id)];
    const ok = r ? !!r.updated : false;
    const errors = r?.errors?.length ? r.errors.map(e => e.message || String(e)) : [];
    return { offer_id: p.offer_id, price: p.price, ok, errors };
  });
  return {
    ok: details.filter(d => d.ok).length,
    fail: details.filter(d => !d.ok).length,
    details,
  };
}

// focusMode разводит два домена верхнего уровня (Цены / Репрайсер — см. OzonSection.js)
// на одном и том же компоненте: вкладки "Цены/Акции/Журнал" живут в домене "Цены",
// "Мониторинг цены/Корректировка медианы/Соблюдение РРЦ/Цена под рентабельность" —
// в отдельном домене "Репрайсер", каждая отдельной вкладкой (раньше 3 последних были
// вложенными режимами внутри одной вкладки "Репрайсер" — подняли на уровень выше).
// Гейты (can()) на сами инструменты не меняются, меняется только то, какие вкладки
// видны из какой точки входа.
const PRICES_DOMAIN_TABS = ['prices', 'promotions', 'log'];
const REPRICER_DOMAIN_TABS = ['vitrine', 'median', 'rrp', 'profit'];

function OzonPricesPanel({ accountId, workspaceId, focusMode = 'prices' }) {
  const { request, showStatus } = useApi();

  // ── Operation log (shared across all subtabs) ────────────────────────────
  const [opLog, setOpLog] = useState([]);
  const addLog = useCallback((entry) => {
    setOpLog(prev => [{ ...entry, ts: new Date() }, ...prev].slice(0, 200));
  }, []);

  // ── Server-side price change log (full audit trail, all tools) ──────────
  const LOG_PAGE_SIZE = 50;
  const [serverLog, setServerLog] = useState([]);
  const [serverLogTotal, setServerLogTotal] = useState(0);
  const [serverLogOffset, setServerLogOffset] = useState(0);
  const [serverLogSource, setServerLogSource] = useState('');
  const [serverLogLoading, setServerLogLoading] = useState(false);

  const loadServerLog = useCallback(async (offset, source) => {
    if (!accountId) return;
    setServerLogLoading(true);
    try {
      const { items, total } = await listPriceChangeLog({
        request, accountId, limit: LOG_PAGE_SIZE, offset, source: source || undefined,
      });
      setServerLog(prev => (offset === 0 ? items : [...prev, ...items]));
      setServerLogTotal(total);
      setServerLogOffset(offset);
    } catch { /* ignore */ } finally {
      setServerLogLoading(false);
    }
  }, [request, accountId]);

  // ── Shared ────────────────────────────────────────────────────────────────
  // Подвкладки — по тарифу кабинета: вкладка, которой нет в тарифе, не показывается,
  // вместо того чтобы открываться с 403 «Ошибка загрузки».
  const { hasFeature } = usePlanFeatures();
  const domainTabs = [
    { key: 'prices', label: 'Цены', toolKey: 'ozon_prices' },
    { key: 'vitrine', label: 'Мониторинг цены', toolKey: 'ozon_price_monitoring' },
    { key: 'median', label: 'Корректировка медианы', toolKey: 'ozon_repricer' },
    { key: 'rrp', label: 'Соблюдение РРЦ', toolKey: 'ozon_map_repricer' },
    { key: 'profit', label: 'Цена под рентабельность', toolKey: 'ozon_repricer_profit' },
    { key: 'promotions', label: 'Акции', toolKey: 'ozon_promotions' },
    { key: 'log', label: 'Журнал', toolKey: 'ozon_price_journal' },
  ].filter(({ key, toolKey }) => (
    (focusMode === 'repricer' ? REPRICER_DOMAIN_TABS : PRICES_DOMAIN_TABS).includes(key) && hasFeature(toolKey)
  ));
  // Подвкладка — параметр адреса ?sub: F5, ссылка и «Назад» её не теряют. Нет в адресе или её нет
  // в тарифе — показываем первую доступную.
  const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
  const urlSub = getUrlParam('sub');
  const subTab = domainTabs.some(tab => tab.key === urlSub)
    ? urlSub
    : (domainTabs[0]?.key || (focusMode === 'repricer' ? 'vitrine' : 'prices'));
  const setSubTab = useCallback((key) => updateUrlParams({ sub: key }, { push: true }), [updateUrlParams]);
  const [products, setProducts] = useState([]);
  const [priceMap, setPriceMap] = useState({});
  const [promoMap, setPromoMap] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);

  // ── Price table search / filters ─────────────────────────────────────────
  const [priceSearch, setPriceSearch] = useState('');
  const [hideOutOfStock, setHideOutOfStock] = useState(false);

  // ── Price change modal + schedules ───────────────────────────────────────
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceTableSelected, setPriceTableSelected] = useState(new Set());
  const [activeSchedules, setActiveSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // ── Promotions tab: per-SKU overview ──────────────────────────────────────
  const [promoView, setPromoView] = useState('prices');
  const [overviewItems, setOverviewItems] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [overviewSearch, setOverviewSearch] = useState('');
  const [overviewOnlyParticipating, setOverviewOnlyParticipating] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  const [runningExitAll, setRunningExitAll] = useState(false);
  const [runningJoinAll, setRunningJoinAll] = useState(false);
  const [runningJoinWithBoundedExit, setRunningJoinWithBoundedExit] = useState(false);

  // ── Promotions automation (auto-exit / auto-join scheduler) ──────────────
  const [autoConfig, setAutoConfig] = useState({ max_discount_percent: 10 });
  const [loadingAutoConfig, setLoadingAutoConfig] = useState(false);
  const [savingAutoConfig, setSavingAutoConfig] = useState(false);
  const [runningAutoNow, setRunningAutoNow] = useState(false);
  const [autoLog, setAutoLog] = useState([]);
  const [autoConfigLoaded, setAutoConfigLoaded] = useState(false);

  // ── Repricer mode ─────────────────────────────────────────────────────────
  // repricerMode больше не отдельный state — режим репрайсера теперь и есть
  // subTab ('median' | 'rrp' | 'profit'), три сиблинг-вкладки вместо вложенного
  // селектора внутри одной вкладки "Репрайсер".
  const [medianSubMode, setMedianSubMode] = useState('auto');

  // ── Auto config form ──────────────────────────────────────────────────────
  const [autoView, setAutoView] = useState('night');
  const [nightCfg, setNightCfg] = useState({ timeFrom: '23:50', percent: '10' });
  const [dayCfg, setDayCfg] = useState({ timeFrom: '09:10', mode: 'keep', percent: '0' });
  const [overrides, setOverrides] = useState({ night: {}, day: {} });
  const [selected, setSelected] = useState(new Set());
  // Снятые галочки сохраняются в конфиг как исключения: новые товары ассортимента
  // по умолчанию в медиане, снятые — нет.
  const excludedRef = useRef(new Set());
  const [autoSearch, setAutoSearch] = useState('');
  const [medPageSize, setMedPageSize] = useState(50);
  const [autoPage, setAutoPage] = useState(0);
  const [customPage, setCustomPage] = useState(0);

  // ── Custom config form ────────────────────────────────────────────────────
  const [slots, setSlots] = useState([
    { id: uid(), name: 'Ночной', timeFrom: '23:50', percent: '10' },
  ]);
  const [slotPercs, setSlotPercs] = useState({});
  const [customSearch, setCustomSearch] = useState('');
  const uploadRef = useRef(null);

  // ── Server state ──────────────────────────────────────────────────────────
  const [repricerConfig, setRepricerConfig] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configLoadFailed, setConfigLoadFailed] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  // Для вкладки "Цены": какие offer_id сейчас под активным РРЦ-репрайсером
  // (per-SKU, см. MapRepricerProduct) — чтобы показать бейдж у конкретных строк.
  const [mapRepricerActiveOffers, setMapRepricerActiveOffers] = useState(() => new Set());

  // ── Manual apply ──────────────────────────────────────────────────────────
  const [manualSlot, setManualSlot] = useState('');
  const [applying, setApplying] = useState(false);

  // ── Runs ──────────────────────────────────────────────────────────────────
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [runItems, setRunItems] = useState({});
  const [loadingRunItems, setLoadingRunItems] = useState({});

  // ── Config ↔ state conversion ─────────────────────────────────────────────

  function applyConfigToState(j) {
    if (!j) return;
    excludedRef.current = new Set((j.excluded_offer_ids || []).map(String));
    setSelected(prev => (products.length
      ? new Set(products.map(p => p.offer_id).filter(id => !excludedRef.current.has(id)))
      : prev));
    if (j.mode === 'custom') {
      setMedianSubMode('custom');
      const custom = j.custom || {};
      const loaded = (custom.slots || []).map(s => ({
        id: String(s.id || uid()),
        name: s.name || '',
        timeFrom: s.time_from || '00:00',
        percent: String(s.percent ?? '0'),
      }));
      if (loaded.length) setSlots(loaded);
      setSlotPercs(custom.product_slots || {});
    } else {
      setMedianSubMode('auto');
      const auto = j.auto || {};
      const night = auto.night || {};
      const day = auto.day || {};
      setNightCfg({ timeFrom: night.time_from || '23:50', percent: String(night.percent ?? '10') });
      setDayCfg({ timeFrom: day.time_from || '09:10', mode: day.mode || 'keep', percent: String(day.percent ?? '0') });
      setOverrides({ night: night.product_overrides || {}, day: day.product_overrides || {} });
    }
  }

  function buildConfigJson() {
    if (medianSubMode === 'custom') {
      return {
        mode: 'custom',
        custom: {
          slots: slots.map(s => ({ id: s.id, name: s.name, time_from: s.timeFrom, percent: parseFloat(s.percent) || 0 })),
          product_slots: slotPercs,
        },
      };
    }
    const excluded = products.length
      ? products.map(p => p.offer_id).filter(id => !selected.has(id))
      : Array.from(excludedRef.current);
    return {
      mode: 'auto',
      excluded_offer_ids: excluded,
      auto: {
        night: {
          time_from: nightCfg.timeFrom,
          percent: parseFloat(nightCfg.percent) || 0,
          product_overrides: overrides.night,
        },
        day: {
          time_from: dayCfg.timeFrom,
          mode: dayCfg.mode,
          percent: parseFloat(dayCfg.percent) || 0,
          product_overrides: overrides.day,
        },
      },
    };
  }

  // ── Load repricer data ────────────────────────────────────────────────────

  const loadRepricerData = useCallback(async () => {
    setConfigLoading(true);
    setConfigLoadFailed(false);
    try {
      const data = await getRepricerConfig({ request, accountId });
      setRepricerConfig(data.config);
      setScheduledJobs(data.scheduled_jobs || []);
      if (data.config) {
        setIsActive(data.config.is_active);
        applyConfigToState(data.config.config_json);
      }
    } catch (e) {
      // Пустую форму поверх сохранённого конфига сохранять нельзя — иначе
      // сбой загрузки молча затирает настройки.
      setConfigLoadFailed(true);
      showStatus('error', 'Не удалось загрузить настройки медианы: ' + (e?.message || ''));
    } finally {
      setConfigLoading(false);
    }
  }, [request, accountId]); // eslint-disable-line

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const data = await getRepricerRuns({ request, accountId });
      setRuns(data.runs || []);
    } catch { /* ignore */ } finally {
      setRunsLoading(false);
    }
  }, [request, accountId]);

  useEffect(() => {
    // Было 'repricer' — после разнесения на сиблинг-вкладки (Медиана/РРЦ/Рентабельность)
    // такого subTab больше не существует, из-за чего конфиг "Корректировка медианы"
    // переставал загружаться вообще. Эти данные (getRepricerConfig/loadRuns) относятся
    // именно к режиму "Медиана" — РРЦ и Рентабельность грузят себя сами как отдельные
    // компоненты (MapRepricerPanel/OzonProfitabilityPanel).
    if (subTab === 'median') {
      loadRepricerData();
      loadRuns();
    }
  }, [subTab]); // eslint-disable-line

  // На вкладке "Цены" — знать, активна ли Медиана (аккаунт-уровень) и какие
  // SKU под активным РРЦ-репрайсером (per-SKU), чтобы пометить их в таблице.
  useEffect(() => {
    if (subTab !== 'prices') return;
    getRepricerConfig({ request, accountId })
      .then((data) => setIsActive(Boolean(data?.config?.is_active)))
      .catch(() => {});
    mapRepricerListProducts({ request, accountId })
      .then((list) => setMapRepricerActiveOffers(new Set(
        (list || []).filter((p) => p.is_active).map((p) => p.offer_id)
      )))
      .catch(() => {});
    // eslint-disable-next-line
  }, [subTab, accountId]);

  useEffect(() => {
    if (subTab === 'log') {
      loadServerLog(0, serverLogSource);
    }
  }, [subTab, serverLogSource]); // eslint-disable-line

  // ── Save config ───────────────────────────────────────────────────────────

  async function handleSaveConfig(activeOverride) {
    if (configLoadFailed) {
      showStatus('error', 'Настройки не загрузились — обновите страницу, чтобы не затереть сохранённые.');
      return;
    }
    const active = activeOverride !== undefined ? activeOverride : isActive;
    setConfigSaving(true);
    try {
      const data = await saveRepricerConfig({
        request, accountId,
        config_json: buildConfigJson(),
        is_active: active,
      });
      setRepricerConfig(data.config);
      setScheduledJobs(data.scheduled_jobs || []);
      setIsActive(data.config.is_active);
      showStatus('success', active ? 'Конфиг сохранён, расписание активировано' : 'Конфиг сохранён');
    } catch {
      showStatus('error', 'Ошибка сохранения конфига');
    } finally {
      setConfigSaving(false);
    }
  }

  // ── Manual apply ──────────────────────────────────────────────────────────

  function getAvailableSlots() {
    if (medianSubMode === 'auto') return [{ key: 'night', label: 'Ночной' }, { key: 'day', label: 'Дневной' }];
    return slots.map(s => ({ key: String(s.id), label: s.name }));
  }

  async function handleManualApply() {
    const avail = getAvailableSlots();
    const slotName = manualSlot || avail[0]?.key;
    if (!slotName) { showStatus('info', 'Нет слотов'); return; }
    if (!repricerConfig) { showStatus('info', 'Сначала сохраните конфиг'); return; }
    if (medianSubMode === 'custom') {
      const savedSlots = repricerConfig.config_json?.custom?.slots || [];
      if (!savedSlots.some(s => String(s.id) === String(slotName))) {
        showStatus('info', 'Слот ещё не сохранён — нажмите «Сохранить конфиг»');
        return;
      }
    }
    setApplying(true);
    try {
      const run = await applyRepricerManual({ request, accountId, slot_name: slotName });
      const slotTitle = availableSlots.find(item => item.key === slotName)?.label || slotLabel(slotName);
      showStatus('success', `Слот «${slotTitle}» выполнен: обновлено ${run.total_updated}, ошибок ${run.total_errors}`);
      await loadRuns();
    } catch (e) {
      showStatus('error', e?.message || 'Ошибка запуска');
    } finally {
      setApplying(false);
    }
  }

  // ── Run items expand ──────────────────────────────────────────────────────

  async function handleExpandRun(runId) {
    if (expandedRun === runId) { setExpandedRun(null); return; }
    setExpandedRun(runId);
    if (runItems[runId]) return;
    setLoadingRunItems(p => ({ ...p, [runId]: true }));
    try {
      const data = await getRepricerRunItems({ request, accountId, runId });
      setRunItems(p => ({ ...p, [runId]: data.items || [] }));
    } catch {
      setRunItems(p => ({ ...p, [runId]: [] }));
    } finally {
      setLoadingRunItems(p => ({ ...p, [runId]: false }));
    }
  }

  // ── Price tab helpers ─────────────────────────────────────────────────────

  const doFetchPrices = useCallback(async (items) => {
    const offerIds = items.map(p => p.offer_id).filter(Boolean);
    if (!offerIds.length) return;
    setLoadingPrices(true);
    try {
      const [priceItems, parserPrices] = await Promise.all([
        fetchPrices({ request, accountId, offerIds }),
        fetchParserCurrentPrices({ request, accountId }).catch(() => []),
      ]);
      const map = {};
      for (const it of priceItems) map[it.offer_id] = it;
      setPriceMap(map);

      // parser-hub keys vitrine prices by marketplace SKU, not offer_id — join via assortment
      const skuToOfferId = {};
      for (const p of items) {
        if (p.sku) skuToOfferId[String(p.sku)] = p.offer_id;
      }
      const promo = {};
      for (const row of parserPrices) {
        const offerId = skuToOfferId[String(row.sku)];
        if (!offerId || row.vitrine_price == null) continue;
        promo[offerId] = {
          price: row.vitrine_price,
          checked_at: row.vitrine_observed_at || row.observed_at,
        };
      }
      setPromoMap(promo);
    } catch (err) {
      showStatus('error', err?.message || 'Не удалось получить цены из OZON');
    } finally {
      setLoadingPrices(false);
    }
  }, [request, accountId, showStatus]);

  const handleLoadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const items = await loadPriceProducts({ request, accountId });
      setProducts(items);
      setSelected(new Set(items.map(p => p.offer_id).filter(id => !excludedRef.current.has(id))));
      await doFetchPrices(items);
    } catch {
      showStatus('error', 'Не удалось загрузить ассортимент');
    } finally {
      setLoadingProducts(false);
    }
  }, [request, accountId, showStatus, doFetchPrices]);

  const handleFetchPrices = useCallback(async () => {
    await doFetchPrices(products);
  }, [products, doFetchPrices]);

  const handleLoadOverview = useCallback(async () => {
    setLoadingOverview(true);
    try {
      const items = await getPromotionsOverview({ request, accountId });
      setOverviewItems(items);
      setOverviewLoaded(true);
    } catch {
      showStatus('error', 'Не удалось проверить участие в акциях');
    } finally {
      setLoadingOverview(false);
    }
  }, [request, accountId, showStatus]);

  const filteredOverview = overviewItems.filter(it => {
    if (overviewOnlyParticipating && !it.in_promotion) return false;
    return matchesSearch(it, overviewSearch);
  });
  const overviewParticipatingCount = overviewItems.filter(it => it.in_promotion).length;

  const handleToggleRowSetting = useCallback(async (item, field) => {
    const nextValue = !item[field];
    const patch = { [field]: nextValue };
    setSavingRowId(item.product_id);
    setOverviewItems(prev => prev.map(it => (it.product_id === item.product_id ? { ...it, ...patch } : it)));
    try {
      await savePromotionProductSetting({
        request, accountId, productId: item.product_id,
        autoExitEnabled: field === 'auto_exit_enabled' ? nextValue : item.auto_exit_enabled,
        autoJoinEnabled: field === 'auto_join_enabled' ? nextValue : item.auto_join_enabled,
        maxDiscountPercent: item.max_discount_percent,
      });
    } catch {
      showStatus('error', 'Не удалось сохранить настройку');
      setOverviewItems(prev => prev.map(it => (it.product_id === item.product_id ? { ...it, [field]: !nextValue } : it)));
    } finally {
      setSavingRowId(null);
    }
  }, [request, accountId, showStatus]);

  const handleChangeRowThreshold = useCallback(async (item, rawValue) => {
    const nextValue = Number(rawValue);
    if (!Number.isFinite(nextValue)) return;
    const prevValue = item.max_discount_percent;
    setOverviewItems(prev => prev.map(it => (it.product_id === item.product_id ? { ...it, max_discount_percent: nextValue } : it)));
    try {
      await savePromotionProductSetting({
        request, accountId, productId: item.product_id,
        autoExitEnabled: item.auto_exit_enabled, autoJoinEnabled: item.auto_join_enabled,
        maxDiscountPercent: nextValue,
      });
    } catch {
      showStatus('error', 'Не удалось сохранить порог');
      setOverviewItems(prev => prev.map(it => (it.product_id === item.product_id ? { ...it, max_discount_percent: prevValue } : it)));
    }
  }, [request, accountId, showStatus]);

  const handleRunExitAll = useCallback(async () => {
    if (!window.confirm('Вывести ВСЕ товары из ВСЕХ акций прямо сейчас? Действие необратимо.')) return;
    setRunningExitAll(true);
    try {
      const result = await runPromotionAutomationExitAll({ request, accountId });
      showStatus('success', `Выведено из акций: ${result.exited || 0}`);
      await handleLoadOverview();
    } catch {
      showStatus('error', 'Не удалось выполнить автовыход');
    } finally {
      setRunningExitAll(false);
    }
  }, [request, accountId, showStatus, handleLoadOverview]);

  const handleRunJoinAll = useCallback(async () => {
    const pct = Number(autoConfig.max_discount_percent) || 0;
    if (!window.confirm(`Добавить ВСЕ подходящие товары в акции со скидкой меньше ${pct}%? Действие необратимо.`)) return;
    setRunningJoinAll(true);
    try {
      const result = await runPromotionAutomationJoinAll({ request, accountId, maxDiscountPercent: pct });
      showStatus('success', `Добавлено в акции: ${result.joined || 0}`);
      await handleLoadOverview();
    } catch {
      showStatus('error', 'Не удалось выполнить автовход');
    } finally {
      setRunningJoinAll(false);
    }
  }, [request, accountId, showStatus, handleLoadOverview, autoConfig.max_discount_percent]);

  const handleRunJoinWithBoundedExit = useCallback(async () => {
    const pct = Number(autoConfig.max_discount_percent) || 0;
    if (!window.confirm(
      `Автовход со скидкой меньше ${pct}% и автовыход из акций со скидкой выше ${pct}%? Действие необратимо.`
    )) return;
    setRunningJoinWithBoundedExit(true);
    try {
      const result = await runPromotionAutomationJoinWithBoundedExit({ request, accountId, maxDiscountPercent: pct });
      showStatus('success', `Добавлено в акции: ${result.joined || 0}, выведено из неподходящих: ${result.exited || 0}`);
      await handleLoadOverview();
    } catch {
      showStatus('error', 'Не удалось выполнить автовход с автовыходом');
    } finally {
      setRunningJoinWithBoundedExit(false);
    }
  }, [request, accountId, showStatus, handleLoadOverview, autoConfig.max_discount_percent]);

  const loadAutoConfig = useCallback(async () => {
    if (!accountId) return;
    setLoadingAutoConfig(true);
    try {
      const cfg = await getPromotionAutomationConfig({ request, accountId });
      setAutoConfig({ max_discount_percent: cfg.max_discount_percent ?? 10 });
      setAutoConfigLoaded(true);
    } catch {
      showStatus('error', 'Не удалось загрузить настройки автоматизации');
    } finally {
      setLoadingAutoConfig(false);
    }
  }, [request, accountId, showStatus]);

  const loadAutoLog = useCallback(async () => {
    if (!accountId) return;
    try {
      const items = await listPromotionAutomationLog({ request, accountId, limit: 100 });
      setAutoLog(items);
    } catch { /* ignore */ }
  }, [request, accountId]);

  useEffect(() => {
    if (subTab === 'promotions' && accountId && !autoConfigLoaded) {
      loadAutoConfig();
      loadAutoLog();
    }
  }, [subTab, accountId, autoConfigLoaded, loadAutoConfig, loadAutoLog]);

  const handleSaveAutoConfig = useCallback(async (maxDiscountPercent) => {
    setSavingAutoConfig(true);
    try {
      const cfg = await savePromotionAutomationConfig({
        request, accountId, config: { max_discount_percent: maxDiscountPercent },
      });
      setAutoConfig({ max_discount_percent: cfg.max_discount_percent ?? 10 });
      showStatus('success', 'Порог сохранён');
    } catch {
      showStatus('error', 'Не удалось сохранить настройки');
    } finally {
      setSavingAutoConfig(false);
    }
  }, [request, accountId, showStatus]);

  const handleRunAutoNow = useCallback(async () => {
    setRunningAutoNow(true);
    try {
      const result = await runPromotionAutomationNow({ request, accountId });
      showStatus('success', `Готово: выведено ${result.exited || 0}, добавлено ${result.joined || 0}`);
      await loadAutoLog();
      await handleLoadOverview();
    } catch {
      showStatus('error', 'Не удалось запустить автоматизацию');
    } finally {
      setRunningAutoNow(false);
    }
  }, [request, accountId, showStatus, loadAutoLog, handleLoadOverview]);

  // A product can be enrolled in more than one promotion at once — this removes
  // just the one `participation` entry, not the whole row.
  const handleRemoveFromPromotion = useCallback(async (item, participation) => {
    if (!participation?.action_id) return;
    try {
      await deactivatePromotionProducts({
        request, accountId, actionId: participation.action_id, productIds: [Number(item.product_id)],
      });
      showStatus('success', `Товар убран из «${participation.action_name || participation.action_id}»`);
      setOverviewItems(prev => prev.map(it => {
        if (it.product_id !== item.product_id) return it;
        const remaining = (it.participations || []).filter(p => p.action_id !== participation.action_id);
        const first = remaining[0];
        return {
          ...it,
          participations: remaining,
          in_promotion: remaining.length > 0,
          action_id: first ? first.action_id : null,
          action_name: first ? first.action_name : '',
          action_price: first ? first.action_price : (it.best_action_price || ''),
        };
      }));
    } catch {
      showStatus('error', 'Не удалось убрать товар из акции');
    }
  }, [request, accountId, showStatus]);

  // Manually join a specific qualifying promotion at a chosen price. `price` must not
  // exceed candidate.action_price (the max OZON allows — a deeper discount is fine).
  const handleJoinPromotion = useCallback(async (item, candidate, price) => {
    const finalPrice = Math.min(Number(price) || candidate.action_price, candidate.action_price);
    try {
      const result = await activatePromotionProducts({
        request, accountId, actionId: candidate.action_id,
        products: [{ product_id: Number(item.product_id), action_price: finalPrice }],
      });
      if ((result.rejected || []).length > 0) {
        showStatus('error', 'OZON отклонил добавление в акцию');
        return;
      }
      showStatus('success', `Товар добавлен в «${candidate.action_name}»`);
      setOverviewItems(prev => prev.map(it => {
        if (it.product_id !== item.product_id) return it;
        const entry = { action_id: candidate.action_id, action_name: candidate.action_name, action_price: finalPrice };
        const already = (it.participations || []).some(p => p.action_id === entry.action_id);
        const participations = already
          ? it.participations.map(p => (p.action_id === entry.action_id ? entry : p))
          : [...(it.participations || []), entry];
        return { ...it, participations, in_promotion: true };
      }));
    } catch {
      showStatus('error', 'Не удалось добавить товар в акцию');
    }
  }, [request, accountId, showStatus]);

  useEffect(() => {
    if (subTab === 'promotions' && promoView === 'auto' && accountId && !overviewLoaded && !loadingOverview) {
      handleLoadOverview();
    }
  }, [subTab, promoView, accountId, overviewLoaded, loadingOverview, handleLoadOverview]);

  // Auto-load cached assortment + prices on mount / account switch — no need to
  // press "Загрузить из ассортимента" first, the button stays for manual refresh.
  useEffect(() => {
    if (accountId) {
      handleLoadProducts();
    }
  }, [accountId]); // eslint-disable-line

  // ── Price change + schedules ──────────────────────────────────────────────

  const loadActiveSchedules = useCallback(async () => {
    if (!accountId) return;
    setLoadingSchedules(true);
    try {
      const schedules = await listPriceSchedules({ request, accountId, status: 'active' });
      setActiveSchedules(schedules);
    } catch { /* ignore */ } finally {
      setLoadingSchedules(false);
    }
  }, [request, accountId]);

  useEffect(() => {
    if (subTab === 'prices' && products.length > 0) {
      loadActiveSchedules();
    }
  }, [subTab, products.length]); // eslint-disable-line

  const handleApplyNow = useCallback(async (prices) => {
    const result = await bulkChangePrice({ request, accountId, prices });
    const { ok, fail, details } = parseOzonPriceResult(result, prices);
    addLog({ op: 'Изменить цену (Цены)', source: 'prices', total: prices.length, ok, fail, details });
    showStatus(fail > 0 ? 'warning' : 'success',
      fail > 0 ? `Обновлено: ${ok}, ошибок: ${fail}` : `Обновлено ${ok} цен`);
    await doFetchPrices(products);
  }, [request, accountId, products, showStatus, doFetchPrices, addLog]);

  const handleCreateSchedule = useCallback(async (items, days) => {
    const result = await createPriceSchedule({ request, accountId, items, days });
    showStatus('success', `Расписание создано: ${items.length} арт. за ${days} дн.`);
    setActiveSchedules(prev => [result, ...prev]);
  }, [request, accountId, showStatus]);

  const handleCancelSchedule = useCallback(async (scheduleId) => {
    try {
      await cancelPriceSchedule({ request, accountId, scheduleId });
      setActiveSchedules(prev => prev.filter(s => s.id !== scheduleId));
      showStatus('success', 'Расписание отменено');
    } catch (e) {
      showStatus('error', 'Не удалось отменить расписание: ' + (e?.message || ''));
    }
  }, [request, accountId, showStatus]);

  // ── Price table: merged rows for UnifiedTable ────────────────────────────
  const priceRows = useMemo(() => products.map(p => {
    const pr = priceMap[p.offer_id] || {};
    const vitrine = formatVitrinePrice(pr, promoMap[p.offer_id]);
    return {
      offer_id: p.offer_id,
      name: p.name,
      product_id: p.product_id,
      sku: p.sku || '', // не колонка, но по SKU таблицу тоже ищут
      price: pr.price || '',
      old_price: pr.old_price || '',
      min_price: pr.min_price || '',
      vitrine_price: vitrine.text,
      vitrine_stale: vitrine.stale,
      marketing_seller_price: pr.marketing_seller_price || '',
      fbo_commission: pr.fbo_commission || '',
      fbs_commission: pr.fbs_commission || '',
      external_index: pr.external_index || '',
      ozon_index: pr.ozon_index || '',
      color_index: pr.color_index || '',
      stock: Number(pr.stock) || 0,
    };
  }), [products, priceMap, promoMap]);

  const filteredPriceRows = useMemo(() => priceRows.filter(r => {
    if (hideOutOfStock && r.stock <= 0) return false;
    return matchesSearch(r, priceSearch);
  }), [priceRows, hideOutOfStock, priceSearch]);

  const priceColumns = useMemo(() => [
    {
      key: 'offer_id', header: 'Артикул', width: 130, type: 'custom', sticky: true,
      render: value => <span className="ozp__mono">{value}</span>,
    },
    { key: 'name', header: 'Название', width: 280, type: 'text' },
    {
      key: 'product_id', header: 'Product ID', width: 110, type: 'custom',
      render: value => <span className="ozp__mono">{value}</span>,
    },
    {
      key: 'price', header: 'Цена', width: 130, type: 'custom', align: 'right',
      render: (value, row) => {
        const underMap = mapRepricerActiveOffers.has(row.offer_id);
        const underMedian = isActive;
        if (!underMap && !underMedian) return value;
        const title = underMap
          ? 'Цена управляется Репрайсером (Соблюдение РРЦ) — ручное изменение может быть перезаписано автоматикой'
          : 'На кабинете активна Медиана (Репрайсер) — цена может меняться автоматически по расписанию';
        return (
          <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
            {value}
            <span className="ozp__badge" style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)', fontSize: 12 }}>
              ⚡ репрайсер
            </span>
          </span>
        );
      },
    },
    { key: 'old_price', header: 'Старая цена', width: 110, type: 'number', align: 'right' },
    { key: 'min_price', header: 'Мин. цена', width: 100, type: 'number', align: 'right' },
    {
      key: 'vitrine_price', header: 'Витринная', width: 150, type: 'custom', align: 'right',
      render: (value, row) => value
        ? (
          <span className={row.vitrine_stale ? 'ozp__price--muted' : 'ozp__price--green'}
            title={row.vitrine_stale ? 'Товара нет в наличии — показана цена последнего замера' : undefined}>
            {value}
          </span>
        )
        : '—',
    },
    { key: 'marketing_seller_price', header: 'Цена продавца', width: 120, type: 'number', align: 'right' },
    { key: 'fbo_commission', header: '% FBO', width: 90, type: 'number', align: 'right' },
    { key: 'fbs_commission', header: '% FBS', width: 90, type: 'number', align: 'right' },
    { key: 'external_index', header: 'Индекс внешн.', width: 110, type: 'number', align: 'right' },
    { key: 'ozon_index', header: 'Индекс OZON', width: 110, type: 'number', align: 'right' },
    {
      key: 'color_index', header: 'Индекс цены', width: 140, type: 'custom',
      render: value => value
        ? (
          <span className={`ozp__badge ozp__badge--${value.toLowerCase()}`}>
            {COLOR_INDEX_LABELS[value] || value}
          </span>
        )
        : '—',
    },
    { key: 'stock', header: 'Остаток', width: 90, type: 'number', align: 'right' },
  ], [mapRepricerActiveOffers, isActive]);

  const handleExportXlsx = useCallback(() => {
    if (!products.length) return;
    const rows = products.map(p => {
      const pr = priceMap[p.offer_id] || {};
      return {
        'Артикул': p.offer_id, 'Название': p.name, 'Product ID': p.product_id,
        'Цена': pr.price || '', 'Старая цена': pr.old_price || '', 'Мин. цена': pr.min_price || '',
        'Витринная': formatVitrinePrice(pr, promoMap[p.offer_id]).text, 'Цена продавца': pr.marketing_seller_price || '',
        '% FBO': pr.fbo_commission || '', '% FBS': pr.fbs_commission || '',
        'Индекс внешн.': pr.external_index || '', 'Индекс OZON': pr.ozon_index || '',
        'Индекс цены': COLOR_INDEX_LABELS[pr.color_index] || pr.color_index || '',
        'Остаток': pr.stock || 0,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Цены OZON');
    XLSX.writeFile(wb, 'ozon_prices.xlsx');
  }, [products, priceMap, promoMap]);

  // ── Auto repricer form helpers ────────────────────────────────────────────

  const filteredAuto = filterBySearch(products, autoSearch);
  const pagedAuto = paginate(filteredAuto, autoPage, medPageSize);

  const allSelected = filteredAuto.length > 0 && filteredAuto.every(p => selected.has(p.offer_id));

  const toggleAll = useCallback(() => {
    const ids = filteredAuto.map(p => p.offer_id);
    setSelected(prev => {
      const s = new Set(prev);
      allSelected ? ids.forEach(id => s.delete(id)) : ids.forEach(id => s.add(id));
      return s;
    });
  }, [filteredAuto, allSelected]);

  const setOverride = useCallback((offerId, value) => {
    setOverrides(prev => ({
      ...prev,
      [autoView]: { ...prev[autoView], [offerId]: value },
    }));
  }, [autoView]);

  const calcNewPrice = useCallback((offerId) => {
    const base = parseFloat(priceMap[offerId]?.price || '0');
    if (!base) return null;
    const globalPct = parseFloat(autoView === 'night' ? nightCfg.percent : dayCfg.percent) || 0;
    const ov = overrides[autoView][offerId];
    const pct = ov !== undefined && ov !== '' ? parseFloat(ov) : globalPct;
    return Math.round(base * (1 + pct / 100));
  }, [priceMap, autoView, nightCfg.percent, dayCfg.percent, overrides]);

  // ── Custom repricer form helpers ──────────────────────────────────────────

  const addSlot = () => setSlots(prev => [
    ...prev,
    { id: uid(), name: `Слот ${prev.length + 1}`, timeFrom: '00:00', percent: '0' },
  ]);

  const removeSlot = (id) => setSlots(prev => prev.filter(s => s.id !== id));
  const updateSlot = (id, field, value) =>
    setSlots(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));

  const setSlotPerc = useCallback((offerId, slotId, value) => {
    setSlotPercs(prev => ({
      ...prev,
      [offerId]: { ...(prev[offerId] || {}), [slotId]: value },
    }));
  }, []);

  const filteredCustom = filterBySearch(products, customSearch);
  const pagedCustom = paginate(filteredCustom, customPage, medPageSize);

  const handleDownloadTemplate = useCallback(() => {
    const rows = products.map(p => {
      const row = { offer_id: p.offer_id, Название: p.name, 'Текущая цена': priceMap[p.offer_id]?.price || '' };
      slots.forEach(s => { row[`${s.name} ${s.timeFrom} %`] = (slotPercs[p.offer_id] || {})[s.id] || ''; });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Репрайсер');
    XLSX.writeFile(wb, 'repricer_template.xlsx');
  }, [products, priceMap, slots, slotPercs]);

  const handleUploadTemplate = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);
        const newPercs = {};
        rows.forEach(row => {
          const offerId = String(row['offer_id'] || '').trim();
          if (!offerId) return;
          newPercs[offerId] = newPercs[offerId] || {};
          slots.forEach(s => {
            const col = `${s.name} ${s.timeFrom} %`;
            const val = row[col];
            if (val !== undefined && val !== '') newPercs[offerId][s.id] = String(val);
          });
        });
        // Товары, которых нет в файле, сохраняют свои значения.
        setSlotPercs(prev => {
          const merged = { ...prev };
          Object.entries(newPercs).forEach(([offerId, values]) => {
            merged[offerId] = { ...(prev[offerId] || {}), ...values };
          });
          return merged;
        });
        showStatus('success', `Загружено ${rows.length} строк`);
      } catch { showStatus('error', 'Ошибка чтения файла'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }, [slots, showStatus]);

  // ── Render ────────────────────────────────────────────────────────────────

  const noProducts = products.length === 0 && !loadingProducts;
  const availableSlots = getAvailableSlots();

  const promotionsColumns = useMemo(() => [
    { key: 'offer_id', header: 'Артикул', width: 140, type: 'text', sticky: true },
    {
      key: 'auto_join_enabled', header: 'Автовход', width: 90, type: 'custom', sortable: false,
      render: (value, row) => (
        <input type="checkbox" checked={!!value} disabled={savingRowId === row.product_id}
          onChange={e => { e.stopPropagation(); handleToggleRowSetting(row, 'auto_join_enabled'); }} />
      ),
    },
    {
      key: 'max_discount_percent', header: 'Порог, %', width: 100, type: 'number',
      render: (value, row) => (
        <ThresholdCell
          value={value}
          disabled={!row.auto_join_enabled || savingRowId === row.product_id}
          onCommit={raw => handleChangeRowThreshold(row, raw)}
        />
      ),
    },
    {
      key: 'auto_exit_enabled', header: 'Автовыход', width: 95, type: 'custom', sortable: false,
      render: (value, row) => (
        <input type="checkbox" checked={!!value} disabled={savingRowId === row.product_id}
          onChange={e => { e.stopPropagation(); handleToggleRowSetting(row, 'auto_exit_enabled'); }} />
      ),
    },
    {
      key: 'name', header: 'Название', width: 360, type: 'custom',
      render: value => (
        <span title={value || ''} style={{
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3,
        }}>
          {value || '—'}
        </span>
      ),
    },
    {
      key: 'sku', header: 'SKU', width: 110, type: 'custom',
      render: value => value
        ? (
          <a href={`https://ozon.ru/product/${value}`} target="_blank" rel="noopener noreferrer"
            style={{ color: '#519ad6', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>
            {value}
          </a>
        )
        : '—',
    },
    {
      key: 'participations', header: 'Участие', width: 220, type: 'custom', sortable: false,
      render: (value, row) => (!value || value.length === 0) ? (
        <span className="ozp__badge ozp__badge--red">Нет</span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {value.map(p => (
            <span key={p.action_id} className="ozp__badge ozp__badge--green"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 200 }}>
              <span style={{
                maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }} title={p.action_name || String(p.action_id)}>
                {p.action_name || p.action_id}
              </span>
              <button type="button" onClick={e => { e.stopPropagation(); handleRemoveFromPromotion(row, p); }}
                title="Убрать из этой акции"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontWeight: 700, lineHeight: 1 }}>
                ×
              </button>
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'qualifying_actions', header: 'Акции, куда можно войти', width: 260, type: 'custom', sortable: false,
      render: (value, row) => {
        const joinedIds = new Set((row.participations || []).map(p => p.action_id));
        const notJoinedYet = (value || []).filter(c => !joinedIds.has(c.action_id));
        if (notJoinedYet.length === 0) {
          return <span title="Нет акций, где минимально необходимая скидка укладывается в заданный порог">—</span>;
        }
        return (
          <div>
            {notJoinedYet.map(candidate => (
              <QualifyingActionRow key={candidate.action_id} candidate={candidate}
                onJoin={(cand, price) => handleJoinPromotion(row, cand, price)} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'action_price', header: 'Цена по акции', width: 130, type: 'number', align: 'right',
      render: (value, row) => (
        <span title={row.in_promotion ? 'Фактическая цена в текущей акции' : 'Цена, если войти в лучшую подходящую акцию (ещё не применена)'}>
          {value != null ? value : '—'}
        </span>
      ),
    },
    { key: 'price', header: 'Цена без акции', width: 130, type: 'number', align: 'right' },
    {
      key: 'discount_percent', header: 'Скидка, %', width: 110, type: 'number', align: 'right',
      render: (value, row) => (
        <span title={row.in_promotion ? 'Фактическая скидка по действующей акции' : 'Минимально необходимая скидка, чтобы войти в лучшую подходящую акцию'}>
          {value != null ? `${value}%` : '—'}
        </span>
      ),
    },
  ], [savingRowId, handleToggleRowSetting, handleChangeRowThreshold, handleRemoveFromPromotion, handleJoinPromotion]);

  return (
    <div className="ozp">
      {/* Sub-tabs */}
      <div className="account-tabs account-tabs--tertiary">
        {domainTabs
          .map(({ key, label, toolKey }) => (key === 'log' && serverLogTotal ? { key, label: `Журнал (${serverLogTotal})`, toolKey } : { key, label, toolKey }))
          .map(({ key, label, toolKey }) => (
          <button key={key} type="button"
            className={subTab === key ? 'active' : ''}
            onClick={() => setSubTab(key)}>
            {label}
            <ToolHelpButton toolKey={toolKey} />
          </button>
        ))}
      </div>

      {/* ─── PRICES ───────────────────────────────────────────────────────── */}
      {subTab === 'prices' && (
        <div className="ozp__section">
          <div className="ozp__toolbar">
            <button className="btn btn--primary" onClick={handleLoadProducts} disabled={loadingProducts}>
              {loadingProducts ? 'Загрузка…' : 'Загрузить из ассортимента'}
            </button>
            {products.length > 0 && (
              <button className="btn btn--primary" onClick={handleFetchPrices} disabled={loadingPrices}>
                {loadingPrices ? 'Получение цен…' : 'Получить цены'}
              </button>
            )}
            {products.length > 0 && (
              <button className="btn btn--secondary" onClick={handleExportXlsx}>
                Скачать XLSX
              </button>
            )}
            {products.length > 0 && (
              <button className="btn btn--accent" onClick={() => {
                const underRepricer = isActive || Array.from(priceTableSelected).some(id => mapRepricerActiveOffers.has(id));
                if (underRepricer && !window.confirm(
                  'Среди выбранных товаров есть SKU, цену которых уже меняет автоматика (Репрайсер). '
                  + 'Ручное изменение может быть перезаписано следующим запуском репрайсера. Продолжить?'
                )) return;
                setShowPriceModal(true);
              }}>
                {priceTableSelected.size > 0
                  ? `Изменить цену (${priceTableSelected.size})`
                  : 'Изменить цену'}
              </button>
            )}
            {products.length > 0 && (
              <label className="ozp__checkbox-label">
                <input type="checkbox" checked={hideOutOfStock}
                  onChange={e => setHideOutOfStock(e.target.checked)} />
                Убрать товары без остатка
              </label>
            )}
            {products.length > 0 && (
              <span className="ozp__count">{filteredPriceRows.length} товаров</span>
            )}
          </div>

          {products.length > 0 && (
            <UnifiedTable
              tableId="ozon-prices-table"
              columns={priceColumns}
              data={filteredPriceRows}
              getRowKey={row => row.offer_id}
              search={priceSearch}
              onSearchChange={setPriceSearch}
              selectable
              selectedRows={Array.from(priceTableSelected)}
              onSelectionChange={keys => setPriceTableSelected(new Set(keys))}
              exportFilename="ozon-prices"
            />
          )}

          {/* Active price schedules */}
          {activeSchedules.length > 0 && (
            <div className="ozp__schedules">
              <div className="ozp__sched-hdr">
                Активные расписания цен
                <button className="ozp__sched-refresh" onClick={loadActiveSchedules} disabled={loadingSchedules}>
                  {loadingSchedules ? '…' : '↻'}
                </button>
              </div>
              {activeSchedules.map(s => (
                <div key={s.id} className="ozp__sched-row">
                  <span className="ozp__sched-info">
                    {s.days_total} дн. · {s.total_items} шт. · создано {fmtDate(s.created_at)}
                  </span>
                  <button className="ozp__sched-cancel btn btn--danger btn--sm"
                    onClick={() => handleCancelSchedule(s.id)}>
                    Отменить
                  </button>
                </div>
              ))}
            </div>
          )}

          {noProducts && (
            <div className="empty" style={{ marginTop: 24 }}>
              Нажмите «Загрузить из ассортимента» чтобы начать.
            </div>
          )}
        </div>
      )}

      {/* ─── PRICE CHANGE MODAL ────────────────────────────────────────────── */}
      {showPriceModal && (
        <PriceChangeModal
          products={products}
          priceMap={priceMap}
          selectedIds={priceTableSelected}
          onClose={() => setShowPriceModal(false)}
          onApplyNow={handleApplyNow}
          onSchedule={handleCreateSchedule}
        />
      )}

      {/* ─── REPRICER: Корректировка медианы / Соблюдение РРЦ / Цена под рентабельность ───
           Раньше это были вложенные режимы внутри одной вкладки "Репрайсер" — теперь
           три сиблинг-вкладки на одном уровне с "Мониторинг цены". ────────────────── */}
      {subTab === 'profit' && (
        <div className="ozp__section">
          <OzonProfitabilityPanel accountId={accountId} addLog={addLog} parseOzonPriceResult={parseOzonPriceResult} />
        </div>
      )}

      {subTab === 'rrp' && (
        <div className="ozp__section">
          <MapRepricerPanel accountId={accountId} workspaceId={workspaceId} allProducts={products} />
        </div>
      )}

      {subTab === 'median' && (
        <div className="ozp__section">
            <>
              {/* Sub-mode toggle */}
              <div className="ozp__submodes">
                {[
                  { key: 'auto', label: 'Авто (День / Ночь)' },
                  { key: 'custom', label: 'Самостоятельный' },
                ].map(({ key, label }) => (
                  <button key={key} type="button"
                    className={`ozp__sub-btn${medianSubMode === key ? ' ozp__sub-btn--on' : ''}`}
                    onClick={() => setMedianSubMode(key)}>
                    {label}
                  </button>
                ))}
              </div>

              {/* ── AUTO config ── */}
              {medianSubMode === 'auto' && (
                <div className="ozp__auto">
                  <div className="ozp__sched-toggle">
                    <button type="button"
                      className={`ozp__sched-btn${autoView === 'night' ? ' ozp__sched-btn--on' : ''}`}
                      onClick={() => setAutoView('night')}>
                      Ночной
                    </button>
                    <button type="button"
                      className={`ozp__sched-btn${autoView === 'day' ? ' ozp__sched-btn--on' : ''}`}
                      onClick={() => setAutoView('day')}>
                      Дневной
                    </button>
                  </div>

                  {autoView === 'night' && (
                    <div className="ozp__cfg-row">
                      <span className="ozp__cfg-label">Время запуска (МСК)</span>
                      <input type="time" className="ozp__time-input" value={nightCfg.timeFrom}
                        onChange={e => setNightCfg(p => ({ ...p, timeFrom: e.target.value }))} />
                      <span className="ozp__cfg-label" style={{ marginLeft: 20 }}>Повышение</span>
                      <input type="number" min="0" max="999" className="ozp__pct-input"
                        value={nightCfg.percent}
                        onChange={e => setNightCfg(p => ({ ...p, percent: e.target.value }))} />
                      <span className="ozp__cfg-unit">%</span>
                      <span className="ozp__cfg-hint">Индивидуальный % задаётся в таблице ниже</span>
                    </div>
                  )}

                  {autoView === 'day' && (
                    <div className="ozp__cfg-row">
                      <span className="ozp__cfg-label">Время запуска (МСК)</span>
                      <input type="time" className="ozp__time-input" value={dayCfg.timeFrom}
                        onChange={e => setDayCfg(p => ({ ...p, timeFrom: e.target.value }))} />
                      <span className="ozp__cfg-label" style={{ marginLeft: 20 }}>Режим</span>
                      <label className="ozp__radio">
                        <input type="radio" value="keep" checked={dayCfg.mode === 'keep'}
                          onChange={() => setDayCfg(p => ({ ...p, mode: 'keep' }))} />
                        Оставить базовые цены
                      </label>
                      <label className="ozp__radio">
                        <input type="radio" value="percent" checked={dayCfg.mode === 'percent'}
                          onChange={() => setDayCfg(p => ({ ...p, mode: 'percent' }))} />
                        Повысить на
                      </label>
                      {dayCfg.mode === 'percent' && (
                        <>
                          <input type="number" min="0" max="999" className="ozp__pct-input"
                            value={dayCfg.percent}
                            onChange={e => setDayCfg(p => ({ ...p, percent: e.target.value }))} />
                          <span className="ozp__cfg-unit">%</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Product overrides table */}
                  {!noProducts && (
                    <>
                      <div className="ozp__toolbar" style={{ marginTop: 14 }}>
                        <input type="text" className="ozp__search"
                          placeholder="Поиск по артикулу / названию"
                          value={autoSearch}
                          onChange={e => { setAutoSearch(e.target.value); setAutoPage(0); }} />
                        <span className="ozp__count">{filteredAuto.length} из {products.length} товаров</span>
                      </div>
                      <div className="ozp__table-wrap">
                        <table className="ozp__table">
                          <thead>
                            <tr>
                              <th>
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                              </th>
                              <th className="ozp__th-art">Артикул</th>
                              <th>Название</th>
                              <th className="ozp__td-r">Баз. цена</th>
                              {(autoView === 'night' || dayCfg.mode === 'percent') && (
                                <>
                                  <th className="ozp__td-r">% индив.</th>
                                  <th className="ozp__td-r">Новая цена</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {pagedAuto.rows.map(p => {
                              const isOn = selected.has(p.offer_id);
                              const ov = overrides[autoView][p.offer_id] ?? '';
                              const np = (autoView === 'night' || dayCfg.mode === 'percent')
                                ? calcNewPrice(p.offer_id) : null;
                              return (
                                <tr key={p.offer_id} className={!isOn ? 'ozp__row--dim' : ''}>
                                  <td>
                                    <input type="checkbox" checked={isOn}
                                      onChange={() => setSelected(prev => {
                                        const s = new Set(prev);
                                        isOn ? s.delete(p.offer_id) : s.add(p.offer_id);
                                        return s;
                                      })} />
                                  </td>
                                  <td className="ozp__mono">{p.offer_id}</td>
                                  <td className="ozp__trunc">{p.name}</td>
                                  <td className="ozp__td-r">{priceMap[p.offer_id]?.price || '—'}</td>
                                  {(autoView === 'night' || dayCfg.mode === 'percent') && (
                                    <>
                                      <td className="ozp__td-r">
                                        <input type="number" min="0" max="999"
                                          className="ozp__pct-cell" value={ov}
                                          placeholder={autoView === 'night' ? nightCfg.percent : dayCfg.percent}
                                          onChange={e => setOverride(p.offer_id, e.target.value)} />
                                      </td>
                                      <td className="ozp__td-r ozp__preview">
                                        {np !== null ? np : '—'}
                                      </td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <TablePager page={pagedAuto.page} pageCount={pagedAuto.pageCount} total={filteredAuto.length}
                        pageSize={medPageSize} onPage={setAutoPage}
                        onPageSize={n => { setMedPageSize(n); setAutoPage(0); setCustomPage(0); }} />
                    </>
                  )}
                  {noProducts && (
                    <div className="empty" style={{ marginTop: 12 }}>
                      Перейдите на вкладку «Цены» и загрузите ассортимент для настройки индивидуальных %.
                    </div>
                  )}
                </div>
              )}

              {/* ── CUSTOM config ── */}
              {medianSubMode === 'custom' && (
                <div className="ozp__custom">
                  <div className="ozp__slots">
                    <div className="ozp__slots-header">
                      <span className="ozp__slots-title">Временные слоты</span>
                      <button className="btn btn--secondary btn--sm" onClick={addSlot}>
                        + Добавить слот
                      </button>
                    </div>
                    {slots.length === 0 && (
                      <p className="muted" style={{ fontSize: 13 }}>Нет слотов — добавьте хотя бы один.</p>
                    )}
                    {slots.map(slot => (
                      <div key={slot.id} className="ozp__slot-row">
                        <input className="ozp__slot-name" type="text" value={slot.name}
                          placeholder="Название"
                          onChange={e => updateSlot(slot.id, 'name', e.target.value)} />
                        <span className="ozp__cfg-label">Запуск (МСК)</span>
                        <input type="time" className="ozp__time-input" value={slot.timeFrom}
                          onChange={e => updateSlot(slot.id, 'timeFrom', e.target.value)} />
                        <span className="ozp__cfg-label" style={{ marginLeft: 16 }}>Дефолт</span>
                        <input type="number" min="-100" max="999" className="ozp__pct-input"
                          value={slot.percent}
                          onChange={e => updateSlot(slot.id, 'percent', e.target.value)} />
                        <span className="ozp__cfg-unit">%</span>
                        <button className="ozp__del-btn" onClick={() => removeSlot(slot.id)} title="Удалить">✕</button>
                      </div>
                    ))}
                  </div>

                  <div className="ozp__toolbar" style={{ marginTop: 16 }}>
                    <button className="btn btn--secondary" onClick={handleDownloadTemplate}
                      disabled={!products.length}>Скачать шаблон XLSX</button>
                    <button className="btn btn--secondary" onClick={() => uploadRef.current?.click()}
                      disabled={!slots.length}>Загрузить XLSX</button>
                    <input ref={uploadRef} type="file" accept=".xlsx,.xls"
                      style={{ display: 'none' }} onChange={handleUploadTemplate} />
                    <input type="text" className="ozp__search"
                      placeholder="Поиск по артикулу / названию"
                      value={customSearch}
                      onChange={e => { setCustomSearch(e.target.value); setCustomPage(0); }} />
                    <span className="ozp__count">{filteredCustom.length} из {products.length} товаров</span>
                  </div>
                  <p className="muted" style={{ fontSize: 12, margin: '4px 0 12px' }}>
                    Пустая ячейка — товар не войдёт в слот. Пустой % = дефолт слота.
                  </p>

                  {noProducts ? (
                    <div className="empty">Перейдите на вкладку «Цены» и загрузите ассортимент.</div>
                  ) : slots.length > 0 && (
                    <div className="ozp__table-wrap">
                      <table className="ozp__table">
                        <thead>
                          <tr>
                            <th className="ozp__th-art">Артикул</th>
                            <th>Название</th>
                            <th className="ozp__td-r">Тек. цена</th>
                            {slots.map(s => (
                              <th key={s.id} className="ozp__td-r ozp__slot-th">
                                <span className="ozp__slot-th-name">{s.name}</span>
                                <span className="ozp__slot-th-time">{s.timeFrom}</span>
                                <span className="ozp__slot-th-def">дефолт: {s.percent}%</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedCustom.rows.map(p => (
                            <tr key={p.offer_id}>
                              <td className="ozp__mono">{p.offer_id}</td>
                              <td className="ozp__trunc">{p.name}</td>
                              <td className="ozp__td-r">{priceMap[p.offer_id]?.price || '—'}</td>
                              {slots.map(s => (
                                <td key={s.id} className="ozp__td-r">
                                  <input type="number" min="-100" max="999"
                                    className="ozp__pct-cell"
                                    value={(slotPercs[p.offer_id] || {})[s.id] || ''}
                                    placeholder="—"
                                    onChange={e => setSlotPerc(p.offer_id, s.id, e.target.value)} />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!noProducts && slots.length > 0 && (
                    <TablePager page={pagedCustom.page} pageCount={pagedCustom.pageCount} total={filteredCustom.length}
                      pageSize={medPageSize} onPage={setCustomPage}
                      onPageSize={n => { setMedPageSize(n); setAutoPage(0); setCustomPage(0); }} />
                  )}
                </div>
              )}

              {/* ── Save / Activate bar ── */}
              <div className="ozp__save-bar">
                <button className="btn btn--primary" onClick={() => handleSaveConfig()} disabled={configSaving || configLoadFailed}>
                  {configSaving ? 'Сохранение…' : 'Сохранить конфиг'}
                </button>
                {repricerConfig && (
                  <button
                    className={`btn ${isActive ? 'btn--secondary' : 'btn--success'}`}
                    onClick={() => handleSaveConfig(!isActive)}
                    disabled={configSaving}>
                    {isActive ? 'Деактивировать расписание' : 'Активировать расписание'}
                  </button>
                )}
                {configLoading && <span className="muted" style={{ fontSize: 13 }}>Загрузка…</span>}
              </div>

              {/* ── Status bar ── */}
              {repricerConfig && (
                <div className={`ozp__status-bar${isActive ? ' ozp__status-bar--active' : ''}`}>
                  <span className={`ozp__status-dot${isActive ? ' ozp__status-dot--on' : ''}`} />
                  <span className="ozp__status-label">
                    {isActive ? 'Расписание активно' : 'Расписание отключено'}
                  </span>
                  {scheduledJobs.map(j => (
                    <span key={j.slot_name} className="ozp__status-slot">
                      {slotLabel(j.slot_name)}:{' '}
                      {j.next_run_at
                        ? `следующий запуск ${fmtDT(j.next_run_at)}`
                        : `${String(j.schedule_hour).padStart(2, '0')}:${String(j.schedule_minute).padStart(2, '0')} МСК`}
                      {j.last_run_at && ` · последний ${fmtDT(j.last_run_at)}`}
                    </span>
                  ))}
                </div>
              )}

              {/* ── Manual apply ── */}
              {repricerConfig && (
                <div className="ozp__manual">
                  <span className="ozp__manual-label">Ручной запуск:</span>
                  <select className="ozp__slot-select"
                    value={manualSlot || availableSlots[0]?.key || ''}
                    onChange={e => setManualSlot(e.target.value)}>
                    {availableSlots.map(s => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                  <button className="btn btn--primary btn--sm" onClick={handleManualApply} disabled={applying}>
                    {applying ? 'Запуск…' : 'Применить вручную'}
                  </button>
                  <span className="ozp__manual-warn">⚠ изменяет цены немедленно, только для теста</span>
                </div>
              )}

              {/* ── Run history ── */}
              <div className="ozp__runs">
                <div className="ozp__runs-title">
                  История запусков
                  <button className="btn btn--secondary btn--sm ozp__runs-refresh"
                    onClick={loadRuns} disabled={runsLoading}>
                    {runsLoading ? '…' : '↻'}
                  </button>
                </div>

                {!runsLoading && runs.length === 0 && (
                  <div className="empty" style={{ marginTop: 8 }}>Запусков ещё не было</div>
                )}

                {runs.length > 0 && (
                  <div className="ozp__table-wrap">
                    <table className="ozp__table ozp__runs-table">
                      <thead>
                        <tr>
                          <th>Дата начала</th>
                          <th>Слот</th>
                          <th>Тип</th>
                          <th>Статус</th>
                          <th className="ozp__td-r">Обновлено</th>
                          <th className="ozp__td-r">Ошибок</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map(r => (
                          <Fragment key={r.id}>
                            <tr className="ozp__run-row" onClick={() => handleExpandRun(r.id)}>
                              <td>{fmtDT(r.started_at)}</td>
                              <td>{slotLabel(slots.find(s => String(s.id) === String(r.slot_name))?.name || r.slot_name)}</td>
                              <td>{r.trigger_type === 'scheduled' ? 'Авто' : 'Вручную'}</td>
                              <td>
                                <span className={`ozp__badge ozp__badge--${r.status === 'done' ? 'green' : r.status === 'error' ? 'red' : 'grey'}`}>
                                  {r.status === 'done' ? 'OK' : r.status === 'error' ? 'Ошибка' : r.status}
                                </span>
                              </td>
                              <td className="ozp__td-r">{r.total_updated}</td>
                              <td className="ozp__td-r">{r.total_errors}</td>
                              <td className="ozp__td-r ozp__run-expand">
                                {expandedRun === r.id ? '▲' : '▼'}
                              </td>
                            </tr>
                            {expandedRun === r.id && (
                              <tr>
                                <td colSpan={7} className="ozp__run-items-cell">
                                  {loadingRunItems[r.id] ? (
                                    <div className="muted" style={{ padding: '8px 0' }}>Загрузка…</div>
                                  ) : !(runItems[r.id] || []).length ? (
                                    <div className="muted" style={{ padding: '8px 0' }}>Нет деталей</div>
                                  ) : (
                                    <table className="ozp__table ozp__items-table">
                                      <thead>
                                        <tr>
                                          <th>Артикул</th>
                                          <th>Название</th>
                                          <th className="ozp__td-r">Было</th>
                                          <th className="ozp__td-r">Стало</th>
                                          <th className="ozp__td-r">%</th>
                                          <th>Статус</th>
                                          <th>Ошибка</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(runItems[r.id] || []).map(i => (
                                          <tr key={i.offer_id}>
                                            <td className="ozp__mono">{i.offer_id}</td>
                                            <td className="ozp__trunc">{i.name}</td>
                                            <td className="ozp__td-r">{i.price_before || '—'}</td>
                                            <td className="ozp__td-r">{i.price_after}</td>
                                            <td className="ozp__td-r">{i.percent_applied}</td>
                                            <td>
                                              <span className={`ozp__badge ozp__badge--${i.status === 'ok' ? 'green' : 'red'}`}>
                                                {i.status === 'ok' ? 'OK' : 'Ошибка'}
                                              </span>
                                            </td>
                                            <td className="ozp__trunc ozp__err-msg">{i.error_msg || ''}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
        </div>
      )}

      {/* ─── PROMOTIONS ───────────────────────────────────────────────────── */}
      {subTab === 'promotions' && (
        <div className="ozp__section">
          <div className="ozp__toolbar" style={{ marginBottom: 12 }}>
            <button className={`btn ${promoView === 'prices' ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setPromoView('prices')}>Цены и участие</button>
            <button className={`btn ${promoView === 'auto' ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setPromoView('auto')}>Автоматика (прежняя)</button>
          </div>
          {promoView === 'prices' && <OzonPromoPrices accountId={accountId} />}
          {promoView === 'auto' && (<>
          <div className="ozp__modes" style={{ marginBottom: 20 }}>
            <div className="ozp__toolbar" style={{ flexWrap: 'wrap', gap: 16 }}>
              <span>Порог автовхода — не участвовать, если скидка ≥</span>
              <input type="number" min="0" max="100" step="0.5" style={{ width: 80 }}
                disabled={loadingAutoConfig}
                value={autoConfig.max_discount_percent}
                onChange={e => setAutoConfig({ max_discount_percent: e.target.value })} />
              <span>%</span>
              <button className="btn btn--primary" disabled={savingAutoConfig || loadingAutoConfig}
                onClick={() => handleSaveAutoConfig(Number(autoConfig.max_discount_percent) || 0)}>
                {savingAutoConfig ? 'Сохранение…' : 'Сохранить порог'}
              </button>
              <button className="btn btn--secondary" disabled={runningAutoNow || loadingAutoConfig}
                onClick={handleRunAutoNow}>
                {runningAutoNow ? 'Выполняется…' : 'Запустить по галочкам сейчас'}
              </button>
            </div>
            <div className="ozp__soon" style={{ marginTop: 8 }}>
              Включение — галочками «Автовход»/«Автовыход» у каждого SKU в таблице ниже. Планировщик проверяет их каждые 60 минут.
              Порог здесь — общий: по нему работают кнопки ниже и автовход товаров, у которых в строке порог не задан.
              Порог в строке — свой для автовхода этого товара. «Автовыход» выводит товар из всех акций без учёта порога.
            </div>
            <div className="ozp__toolbar" style={{ marginTop: 12 }}>
              <button className="btn btn--danger" disabled={runningExitAll}
                onClick={handleRunExitAll}>
                {runningExitAll ? 'Выполняется…' : 'Автовыход на всё (сразу, вне зависимости от галочек)'}
              </button>
              <button className="btn btn--danger" disabled={runningJoinAll}
                onClick={handleRunJoinAll}>
                {runningJoinAll ? 'Выполняется…' : `Автовход со скидкой меньше ${autoConfig.max_discount_percent}% на всё (сразу)`}
              </button>
              <button className="btn btn--primary" disabled={runningJoinWithBoundedExit}
                onClick={handleRunJoinWithBoundedExit}
                title="Заходит в акции со скидкой меньше X% и одновременно выходит из акций, где скидка выше X% (а не из всех подряд)">
                {runningJoinWithBoundedExit
                  ? 'Выполняется…'
                  : `Автовход со скидкой меньше ${autoConfig.max_discount_percent}% с автовыходом из неподходящих`}
              </button>
            </div>
            {autoLog.length > 0 && (
              <div className="ozp__table-wrap" style={{ marginTop: 16, maxHeight: 260, overflowY: 'auto' }}>
                <table className="ozp__table">
                  <thead>
                    <tr>
                      <th>Время</th>
                      <th>Действие</th>
                      <th>Товар</th>
                      <th>Акция</th>
                      <th className="ozp__td-r">Скидка</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoLog.map(it => (
                      <tr key={it.id}>
                        <td>{fmtDT(it.created_at)}</td>
                        <td>{it.action === 'exit' ? 'Выход' : 'Вход'}</td>
                        <td className="ozp__mono">{it.offer_id || it.product_id}</td>
                        <td className="ozp__trunc">{it.action_name || '—'}</td>
                        <td className="ozp__td-r">{it.discount_percent != null ? `${it.discount_percent}%` : '—'}</td>
                        <td>
                          <span className={`ozp__badge ozp__badge--${it.status === 'ok' ? 'green' : 'red'}`}>
                            {it.status === 'ok' ? 'OK' : (it.error_msg || 'Ошибка')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="ozp__toolbar">
            <button className="btn btn--primary" onClick={handleLoadOverview} disabled={loadingOverview}>
              {loadingOverview ? 'Проверка…' : 'Обновить'}
            </button>
            {overviewLoaded && (
              <span className="ozp__count">
                {overviewParticipatingCount} из {overviewItems.length} товаров участвует в акциях
              </span>
            )}
            {overviewLoaded && overviewItems.length > 0 && (
              <label className="ozp__checkbox-label">
                <input type="checkbox" checked={overviewOnlyParticipating}
                  onChange={e => setOverviewOnlyParticipating(e.target.checked)} />
                Только участвующие
              </label>
            )}
          </div>

          {!overviewLoaded && !loadingOverview && (
            <div className="empty" style={{ marginTop: 32 }}>
              Нажмите «Обновить», чтобы загрузить участие в акциях и доступные варианты по каждому товару.
            </div>
          )}
          {loadingOverview && (
            <div className="empty" style={{ marginTop: 32 }}>
              Опрашиваем OZON по всем активным акциям — это может занять до минуты на большом ассортименте.
            </div>
          )}

          {overviewLoaded && overviewItems.length > 0 && (
            <UnifiedTable
              tableId="ozon-promotions-overview"
              columns={promotionsColumns}
              data={filteredOverview}
              getRowKey={row => row.product_id}
              search={overviewSearch}
              onSearchChange={setOverviewSearch}
              exportFilename="ozon-promotions"
              loading={loadingOverview}
            />
          )}
          </>)}
        </div>
      )}

      {/* ─── VITRINE MONITOR ─────────────────────────────────────────────── */}
      {subTab === 'vitrine' && (
        <OzonVitrineMonitor accountId={accountId} workspaceId={workspaceId} />
      )}

      {/* ─── LOG ──────────────────────────────────────────────────────────── */}
      {subTab === 'log' && (
        <OzpLog
          entries={serverLog}
          total={serverLogTotal}
          offset={serverLogOffset}
          pageSize={LOG_PAGE_SIZE}
          loading={serverLogLoading}
          source={serverLogSource}
          onSourceChange={setServerLogSource}
          onLoadMore={() => loadServerLog(serverLogOffset + LOG_PAGE_SIZE, serverLogSource)}
          onRefresh={() => loadServerLog(0, serverLogSource)}
        />
      )}
    </div>
  );
}

function fmtDiscount(pct) {
  if (pct == null) return '—';
  // OZON sometimes accepts a product into a promotion at a price ABOVE its
  // current price (a "negative discount") — show that as a markup, not a
  // confusing negative percent.
  return pct < 0 ? `+${Math.abs(pct)}% к цене` : `от ${pct}%`;
}

// Per-row "Порог, %" cell. Was previously an *uncontrolled* input (defaultValue +
// a key that only changed after a successful save) — the account-wide threshold
// input right below it (line ~1568) is fully controlled (value+onChange), so the
// two fields behaved differently: the account-wide one's native +/- spinner moved
// the visible value immediately, while this uncontrolled one only "moved" inside
// the browser's own hidden state until blur, which looked like a dead stepper to
// the user. Making both controlled, with the same step (0.5), makes the +/-
// spinners on both fields behave identically; saving is still deferred to blur
// so we don't fire a network request on every single click.
function ThresholdCell({ value, disabled, onCommit }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  return (
    <input type="number" min="0" max="100" step="0.5" style={{ width: 70 }}
      disabled={disabled}
      value={local}
      onClick={e => e.stopPropagation()}
      onChange={e => setLocal(e.target.value)}
      onBlur={e => onCommit(e.target.value)} />
  );
}

function QualifyingActionRow({ candidate, disabled, onJoin }) {
  const ceilingPrice = candidate.action_price; // max price OZON allows — cannot go higher
  const regularPrice = candidate.price;
  const [price, setPrice] = useState(ceilingPrice);
  const [joining, setJoining] = useState(false);

  useEffect(() => { setPrice(ceilingPrice); }, [ceilingPrice]);

  const numericPrice = price === '' ? null : Number(price);
  const overCeiling = numericPrice != null && numericPrice > ceilingPrice;
  const liveDiscount = (numericPrice != null && regularPrice)
    ? Math.round((regularPrice - numericPrice) / regularPrice * 1000) / 10
    : candidate.discount_percent;

  const handleChange = (e) => {
    const raw = e.target.value;
    setPrice(raw === '' ? '' : Number(raw));
  };

  const handleBlur = () => {
    if (price === '' || Number.isNaN(Number(price)) || Number(price) > ceilingPrice) {
      setPrice(ceilingPrice);
    }
  };

  const handleJoin = async () => {
    const finalPrice = (price === '' || Number(price) > ceilingPrice) ? ceilingPrice : Number(price);
    setJoining(true);
    try {
      await onJoin(candidate, finalPrice);
    } finally {
      setJoining(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 0',
      borderBottom: '1px solid var(--border-color, #e2e8f0)',
    }}>
      <span title={candidate.action_name} style={{
        maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600,
      }}>
        {candidate.action_name || candidate.action_id}
      </span>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        Цена входа:
        <input type="number" value={price}
          disabled={disabled || joining}
          title={`OZON не пропустит цену выше ${ceilingPrice} ₽ для этой акции — это минимально требуемая скидка. Ниже — можно, скидка будет больше.`}
          style={{
            width: 85, padding: '3px 6px',
            border: overCeiling ? '1px solid #d33' : '1px solid var(--border-color, #cbd5e1)',
            borderRadius: 4,
          }}
          onClick={e => e.stopPropagation()}
          onChange={handleChange} onBlur={handleBlur} />
        ₽
        <button type="button" style={{
          border: 'none', background: 'none', cursor: 'pointer', padding: 0,
          textDecoration: 'underline', fontSize: 12, opacity: 0.75,
        }}
          title="Вернуть минимально необходимую скидку"
          onClick={e => { e.stopPropagation(); setPrice(ceilingPrice); }}>
          сброс до {ceilingPrice}
        </button>
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, opacity: 0.75 }}>{fmtDiscount(liveDiscount)}</span>
        <button className="btn btn--secondary" disabled={disabled || joining}
          onClick={e => { e.stopPropagation(); handleJoin(); }}>
          {joining ? '…' : 'Войти'}
        </button>
      </div>
    </div>
  );
}

const LOG_SOURCE_LABELS = {
  manual_update: 'Ручное обновление',
  manual_promo: 'Акции (вручную)',
  price_change: 'Изменение цены',
  price_schedule: 'Плавное расписание',
  repricer: 'Корректировка медианы',
  map_repricer: 'Соблюдение РРЦ',
  bidder_automation: 'Биддер',
  price_guard: 'Защита рентабельности',
  promotion_auto_join: 'Автовход в акции',
  coinvest: 'Контроль соинвеста',
};

const MED_PAGE_SIZES = [50, 100, 250, 500];

function paginate(rows, page, pageSize) {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safe = Math.min(page, pageCount - 1);
  return { rows: rows.slice(safe * pageSize, (safe + 1) * pageSize), page: safe, pageCount };
}

function TablePager({ page, pageCount, total, pageSize, onPage, onPageSize }) {
  if (total <= MED_PAGE_SIZES[0]) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="ozp__toolbar" style={{ marginTop: 10, alignItems: 'center' }}>
      <span className="muted" style={{ fontSize: 13 }}>На странице</span>
      <select value={pageSize} onChange={e => onPageSize(Number(e.target.value))}>
        {MED_PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 0}
        onClick={() => onPage(page - 1)}>←</button>
      <span className="muted" style={{ fontSize: 13 }}>
        {from}–{to} из {total} · стр. {page + 1}/{pageCount}
      </span>
      <button type="button" className="btn btn--secondary btn--sm" disabled={page >= pageCount - 1}
        onClick={() => onPage(page + 1)}>→</button>
    </div>
  );
}

function OzpLog({ entries, total, offset, pageSize, loading, source, onSourceChange, onLoadMore, onRefresh }) {
  function fmtTs(iso) {
    if (!iso) return '—';
    try {
      return fmtDateTime(iso);
    } catch { return iso; }
  }

  function handleDownloadLog() {
    const rows = entries.map(e => ({
      'Дата/время': fmtTs(e.created_at),
      'Раздел': LOG_SOURCE_LABELS[e.source] || e.source || '',
      'Артикул': e.offer_id,
      'Было': e.old_price || '',
      'Стало': e.new_price,
      'Пользователь': e.changed_by_name || 'Автоматически',
      'Статус': e.status === 'ok' ? 'OK' : 'Ошибка',
      'Ошибка': e.error_msg || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 10 }, { wch: 40 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Журнал');
    const date = fmtDate().replace(/\./g, '-');
    XLSX.writeFile(wb, `ozon_price_log_${date}.xlsx`);
  }

  // entries уже содержит все загруженные страницы.
  const hasMore = entries.length < total;

  return (
    <div className="ozp__log">
      <div className="ozp__log-hdr">
        <span>Журнал изменения цен</span>
        <span className="ozp__log-count">{total}</span>
        <select className="ozp__slot-select" value={source} onChange={e => onSourceChange(e.target.value)}>
          <option value="">Все инструменты</option>
          {Object.entries(LOG_SOURCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button className="btn btn--secondary btn--sm" onClick={onRefresh} disabled={loading}>
          {loading ? '…' : '↻ Обновить'}
        </button>
        <button className="btn btn--secondary btn--sm" onClick={handleDownloadLog} disabled={!entries.length}>
          Скачать XLSX
        </button>
      </div>

      {!entries.length && !loading && (
        <div className="empty" style={{ marginTop: 24 }}>
          Изменений цен пока не было — записи появятся после первого изменения в любом инструменте (ручное
          обновление, «Изменить цену», расписание, репрайсер).
        </div>
      )}

      {entries.length > 0 && (
        <div className="ozp__table-wrap">
          <table className="ozp__table">
            <thead>
              <tr>
                <th>Дата/время</th>
                <th>Инструмент</th>
                <th className="ozp__th-art">Артикул</th>
                <th>Название</th>
                <th className="ozp__td-r">Было</th>
                <th className="ozp__td-r">Стало</th>
                <th>Пользователь</th>
                <th>Статус</th>
                <th>Ошибка</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td>{fmtTs(e.created_at)}</td>
                  <td>{LOG_SOURCE_LABELS[e.source] || e.source}</td>
                  <td className="ozp__mono">{e.offer_id}</td>
                  <td className="ozp__trunc">{e.name || '—'}</td>
                  <td className="ozp__td-r">{e.old_price || '—'}</td>
                  <td className="ozp__td-r">{e.new_price}</td>
                  <td>{e.changed_by_name || <span className="muted">Автоматически</span>}</td>
                  <td>
                    <span className={`ozp__badge ${e.status === 'ok' ? 'ozp__badge--green' : 'ozp__badge--red'}`}>
                      {e.status === 'ok' ? 'OK' : 'Ошибка'}
                    </span>
                  </td>
                  <td className="ozp__trunc ozp__err-msg">{e.error_msg || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn--secondary btn--sm" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Загрузка…' : `Показать ещё (${total - entries.length})`}
          </button>
        </div>
      )}
    </div>
  );
}

export default OzonPricesPanel;
